import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Wallet, CheckCircle2, RotateCcw, ChevronRight, Sparkles } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tabipla/ui";
import type { RoundingUnit, SessionResponseDto } from "@tabipla/shared";

export interface SessionListProps {
  /** List of sessions to display */
  sessions: SessionResponseDto[];
  /** Callback when a session is selected */
  onSessionSelect: (sessionId: string) => void;
  /** Whether the user can edit (create/complete/reopen) sessions */
  canEdit: boolean;
  /** Callback to create a new session */
  onCreateSession: (name: string, roundingUnit?: RoundingUnit) => Promise<void>;
  /** Whether session creation is in progress */
  isCreating?: boolean;
  /** Callback to complete a session */
  onCompleteSession: (sessionId: string) => Promise<void>;
  /** Whether session completion is in progress */
  isCompleting?: boolean;
  /** Callback to reopen a session */
  onReopenSession: (sessionId: string) => Promise<void>;
  /** Whether session reopening is in progress */
  isReopening?: boolean;
  /** Optional title override */
  title?: string;
  /** Optional empty state description override */
  emptyDescription?: string;
  /** Optional create dialog description override */
  createDescription?: string;
  /** Optional placeholder for session name input */
  namePlaceholder?: string;
}

export function SessionList({
  sessions,
  onSessionSelect,
  canEdit,
  onCreateSession,
  isCreating = false,
  onCompleteSession,
  isCompleting = false,
  onReopenSession,
  isReopening = false,
  title,
  emptyDescription,
  createDescription,
  namePlaceholder,
}: SessionListProps) {
  const { t } = useTranslation();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSessionName, setNewSessionName] = useState("");
  const [newRoundingUnit, setNewRoundingUnit] = useState<RoundingUnit>(1);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSessionName.trim()) return;

    await onCreateSession(newSessionName, newRoundingUnit);
    setShowCreateDialog(false);
    setNewSessionName("");
    setNewRoundingUnit(1);
  };

  const handleComplete = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await onCompleteSession(sessionId);
  };

  const handleReopen = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await onReopenSession(sessionId);
  };

  // Sort sessions: non-completed first, then by createdAt desc
  const sortedSessions = [...sessions].sort((a, b) => {
    if (a.status !== b.status) {
      // Non-completed (inputting, settling) comes before completed
      if (a.status !== "completed" && b.status === "completed") return -1;
      if (a.status === "completed" && b.status !== "completed") return 1;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center text-lg">
            <Wallet className="mr-2 h-5 w-5" />
            {title ?? t("sessions.title", "精算")}
          </CardTitle>
          {canEdit && (
            <Button size="sm" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t("sessions.create", "新規精算")}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title={t("sessions.empty", "精算がありません")}
              description={
                emptyDescription ?? t("sessions.emptyDescription", "「新規精算」から精算を作成")
              }
              variant="default"
            />
          ) : (
            <div className="space-y-2">
              {sortedSessions.map((session) => (
                <div
                  key={session.id}
                  className={`group cursor-pointer rounded-lg border bg-card transition-all hover:border-primary/50 hover:shadow-md active:scale-[0.995] ${
                    session.status !== "completed" ? "border-primary/30" : "border-border"
                  }`}
                  onClick={() => onSessionSelect(session.id)}
                >
                  <div className="flex items-center gap-3 p-3">
                    {/* アイコン */}
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
                        session.status === "completed"
                          ? "bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400"
                          : "bg-primary/10 text-primary"
                      }`}
                    >
                      {session.status === "completed" ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <Wallet className="h-5 w-5" />
                      )}
                    </div>

                    {/* コンテンツ */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-medium text-foreground">{session.name}</h3>
                        {session.status !== "completed" && (
                          <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2">
                        <span className="text-lg font-bold tabular-nums text-foreground">
                          {formatAmount(session.totalExpenses)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          · {session.expenseCount}
                          {t("sessions.expenseCount", "件")}
                        </span>
                      </div>
                    </div>

                    {/* 右側 */}
                    <div className="flex shrink-0 items-center gap-2">
                      {/* ステータスバッジ */}
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          session.status === "completed"
                            ? "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400"
                            : "bg-primary/10 text-primary"
                        }`}
                      >
                        {session.status === "completed"
                          ? t("sessions.completed", "完了")
                          : t("sessions.active", "進行中")}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>

                  {/* アクションエリア - canEditの場合のみ表示 */}
                  {canEdit && (
                    <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-3 py-2">
                      {session.status !== "completed" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-green-600 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-950"
                          onClick={(e) => handleComplete(session.id, e)}
                          disabled={isCompleting}
                        >
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                          {t("sessions.completeAction", "完了する")}
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={(e) => handleReopen(session.id, e)}
                          disabled={isReopening}
                        >
                          <RotateCcw className="mr-1 h-3.5 w-3.5" />
                          {t("sessions.reopen", "再開")}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Session Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              {t("sessions.createTitle", "新規精算")}
            </DialogTitle>
            <DialogDescription>
              {createDescription ??
                t("sessions.createDescription", "イベントごとにお金を分けて記録")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("sessions.name", "精算名")}</label>
                <Input
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  placeholder={namePlaceholder ?? t("sessions.namePlaceholder", "例: 忘年会 2024")}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("sessions.roundingUnit", "精算の端数処理")}
                </label>
                <Select
                  value={String(newRoundingUnit)}
                  onValueChange={(v) => setNewRoundingUnit(Number(v) as RoundingUnit)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">
                      {t("sessions.rounding1", "1円単位（端数なし）")}
                    </SelectItem>
                    <SelectItem value="10">{t("sessions.rounding10", "10円単位")}</SelectItem>
                    <SelectItem value="100">{t("sessions.rounding100", "100円単位")}</SelectItem>
                    <SelectItem value="1000">{t("sessions.rounding1000", "1000円単位")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "sessions.roundingDescription",
                    "精算金額を丸めます（出費の分割には影響しません）"
                  )}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1 sm:flex-none"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isCreating || !newSessionName.trim()}
                className="flex-1 sm:flex-none"
              >
                {isCreating ? t("common.creating") : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
